import type { ReactNode } from 'react';
import { theme as antdTheme } from 'antd';
import { brandFonts } from '@viq-admin/theme';
import { typeScale } from '@viq-admin/ui';
import { TILE_CAPTION_LINE, TILE_NUMBER_GAP, tileLabelStyle } from './tileStyles';

/**
 * Status tone for a metric tile, expressed as antd theme-token colours below
 * — a generic vocabulary this primitive owns. `leadRules/notesTile.ts` (the
 * ONE feature that currently drives a tone) imports it back from here rather
 * than the other way around, so this shared visual primitive never reaches
 * into a feature module for its own type.
 */
export type MetricTone = 'success' | 'danger';

/**
 * The line under a tile's number. It always takes its line, empty or not, so
 * a tile with a caption ("3 below notes minimum") is no taller than one
 * without and every tile's number sits at the same height. Shared with
 * `ScansGoalCard`.
 */
export function TileCaption({ color, children }: { color: string; children?: ReactNode }) {
  return (
    <div
      style={{
        fontSize: typeScale.caption,
        lineHeight: `${TILE_CAPTION_LINE}px`,
        minHeight: TILE_CAPTION_LINE,
        color,
        marginTop: 6,
      }}
    >
      {children}
    </div>
  );
}

interface Props {
  label: string;
  /** Number to render. Undefined renders '—' so cards line up regardless of
   *  data presence. */
  value: number | undefined;
  /** Optional status tone from theme tokens (lead-rules readiness). Omitted = neutral. */
  tone?: MetricTone;
  /** Short line under the value; carries the tone's meaning in words, not just colour. */
  caption?: string;
}

/**
 * Shared visual primitive for the Batch screen's KPI tiles + Volume & Coverage
 * strip. Ported verbatim from the portal — theme tokens for surface/border/text,
 * the brand headline font for the number.
 *
 * Label, number and caption sit at fixed distances from the top of the tile,
 * and the caption line is always reserved, so the label and number of every
 * tile in a row line up whether or not a tile has a caption.
 */
export function MetricCard({ label, value, tone, caption }: Props) {
  const { token } = antdTheme.useToken();

  const display: string = value === undefined ? '—' : value.toLocaleString('en-US');
  const toneColor = tone === 'danger' ? token.colorError : tone === 'success' ? token.colorSuccess : undefined;

  return (
    <div
      data-tone={tone}
      style={{
        minHeight: 88,
        // A toned tile's border is 1px thicker; take that pixel out of the
        // padding so its label and number stay level with its neighbours'.
        padding: toneColor ? '13px 15px' : '14px 16px',
        borderRadius: token.borderRadius,
        background: token.colorBgContainer,
        borderWidth: toneColor ? 2 : 1,
        borderStyle: 'solid',
        borderColor: toneColor ?? token.colorBorderSecondary,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={tileLabelStyle(token.colorTextSecondary)}>{label}</div>
      <div
        style={{
          fontFamily: brandFonts.headline,
          fontWeight: 700,
          fontSize: typeScale.h2,
          color: token.colorText,
          lineHeight: 1,
          marginTop: TILE_NUMBER_GAP,
        }}
      >
        {display}
      </div>
      <TileCaption color={toneColor ?? token.colorTextSecondary}>{caption}</TileCaption>
    </div>
  );
}
